import type { PageName } from '~/config'
import type { Controller } from './controller'
import type { LocalStateChange } from './stateChange'
import type { Identifiable } from '~/models/identifiable'
import { LocalChanges } from './localChanges'

export abstract class ControllerState<
  Item extends Identifiable,
  ItemCollection extends Item[] | Record<string, Item>,
> {
  protected data!: ItemCollection
  private loading: boolean
  private sideEffect?: () => void
  private update: () => void
  public readonly pageName: string
  private _isActive: boolean

  protected localChanges: LocalChanges<Item> = new LocalChanges<Item>()

  public get isActive(): boolean {
    return this._isActive
  }

  constructor(controller: Controller, sideEffect?: () => void) {
    this.update = controller.update.bind(controller)
    this.pageName = controller.pageName
    this.sideEffect = sideEffect?.bind(controller)
    this.loading = true
    this._isActive = true
  }

  activatePage(pageName: PageName) {
    if (pageName === this.pageName) {
      this._isActive = true
      return
    }
    this._isActive = false
  }

  public setLocalState(stateChanges: LocalStateChange<Item>[]) {
    for (const change of stateChanges) {
      this.localChanges.addStateChange(change)
    }

    this.applyLocalChanges()

    if (this.sideEffect) {
      this.sideEffect()
    }

    this.update()
  }

  public setState(newState: ItemCollection) {
    if (this._isActive === false) {
      return
    }

    const valueWithLocalChanges = this.keepLocalChanges(newState)

    this.data = valueWithLocalChanges
    this.loading = false
    if (this.sideEffect) {
      this.sideEffect()
    }
    this.update()
  }

  public setInitialState(newState: ItemCollection) {
    if (this._isActive === false) {
      return
    }

    const valueWithLocalChanges = this.keepLocalChanges(newState)

    this.data = valueWithLocalChanges
    this.loading = false
  }

  public get state(): ItemCollection {
    return this.data
  }

  public clear() {
    this.data = this.getEmptyState()
    this.loading = false
    this.update()
  }

  public isEmpty(): boolean {
    return !this.data
  }

  public isLoading(): boolean {
    return this.loading
  }

  public setLoading() {
    this.loading = true
  }

  public stopLoading() {
    this.loading = false
    this.update()
  }

  abstract keepLocalChanges(newState: ItemCollection): ItemCollection
  abstract applyLocalChanges(): void
  abstract handleStateAdd(newState: ItemCollection): ItemCollection
  abstract handleStateRemove(newState: ItemCollection): ItemCollection
  abstract handleStateUpdate(newState: ItemCollection): ItemCollection
  abstract getEmptyState(): ItemCollection
}
